import { z } from 'zod'

/**
 * Schemas Zod des mutations DMP declarees dans src/ApiMutationEndpoint.ts.
 *
 * Fichier volontairement LEGER (zod seul) : ApiMutationEndpoint fait partie du shell de l'app
 * (scripts/check-shell-budget.cjs), donc tout ce qu'il importe est charge sur chaque page.
 * Importer ces schemas depuis dmpSchemas.ts y faisait entrer dmpTypes.ts et ameliTypes.ts,
 * soit environ 1 200 lignes de schemas pour quatre petits corps de requete.
 * N'ajouter ici que des schemas sans dependance ; dmpSchemas.ts et dmpTypes.ts les reexportent.
 */

/**
 * Element d'un document Care qui produit plusieurs notes DMP : le code d'un vaccin d'une seance de
 * vaccination V2 (une note par vaccin). Miroir de DmpAlimentationEtatService::normaliserCareItemKey.
 */
export const dmpCareItemKeySchema = z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/)

/**
 * Identite du patient concerne. Miroir de src/Dto/Ameli/DMP/DmpPatientContext.php.
 * `ins` = matricule INS complet (NIR 13 chiffres + cle 2 chiffres, ex. golden 207058575627097) ;
 * `insOid` = OID du domaine (defaut INS-NIR cote API).
 * `dateNaissance` au format AAAAMMJJ (attendu par le CDA / metadonnees XDS).
 */
export const DmpPatientContextSchema = z.object({
  ins: z.string().min(1),
  insOid: z.string().optional(),
  nom: z.string(),
  prenom: z.string(),
  sexe: z.string(),
  dateNaissance: z.string(),
  localId: z.string().optional(),
  insDateCalcul: z.string().optional(),
})
export type DmpPatientContext = z.infer<typeof DmpPatientContextSchema>

/**
 * Corps de PATCH /care/dossier/{id}/dmp-alimentation-auto : bascule du switch « Envoi automatique
 * au DMP » d'un dossier patient (LOT 2a, EX_GEN-1060). Refuse (409) cote API si le patient s'oppose
 * a l'alimentation et que la bascule tente une ACTIVATION.
 */
export const dmpEnvoiAutomatiquePatchSchema = z.object({
  dmpEnvoiAutomatique: z.boolean(),
})
export type DmpEnvoiAutomatiquePatchInput = z.infer<typeof dmpEnvoiAutomatiquePatchSchema>

/**
 * Corps de POST /ameli/dmp/reglage-visibilite : regle de visibilite par defaut de la pharmacie pour
 * un type de document (LOT 2a, EX_GEN-1060 / EX_2.1-1050). `invisibleRepresentantsLegauxMineur` ne
 * s'applique qu'aux patients mineurs (cf. DmpAlimentationAutoService cote API).
 */
export const dmpReglageVisibiliteWriteSchema = z.object({
  careSource: z.enum(['care_compte_rendu', 'care_trod', 'care_vaccination', 'care_documents']),
  masquePs: z.boolean(),
  invisiblePatient: z.boolean(),
  invisibleRepresentantsLegauxMineur: z.boolean(),
})
export type DmpReglageVisibiliteWriteInput = z.infer<typeof dmpReglageVisibiliteWriteSchema>

/**
 * Corps de POST /ameli/dmp/reglage-visibilite : bascule du reglage d'affichage des traits
 * d'identite retournes par TD0.2 dans le bloc patientPerson (EX_0.2-1020), DESACTIVE par defaut.
 * Meme endpoint que dmpReglageVisibiliteWriteSchema ci-dessus, cle distincte (`careSource` absent
 * ici) : cote API, DmpReglageVisibiliteController distingue les deux ecritures sur cette seule cle.
 */
export const dmpReglageAccesWriteSchema = z.object({
  afficherTraitsIdentiteTd02: z.boolean(),
})
export type DmpReglageAccesWriteInput = z.infer<typeof dmpReglageAccesWriteSchema>

/**
 * Corps de POST /ameli/dmp/alimentation-etat : marque un document Care comme RETENU (case cochee
 * dans l'encart d'envoi, LOT 2a, EX_GEN-1060). Aucune transaction DMP : ni prepare ni execute.
 */
export const dmpAlimentationEtatRetenirSchema = z.object({
  careDocumentId: z.number(),
  careSource: z.enum(['care_compte_rendu', 'care_trod', 'care_vaccination', 'care_documents']),
  careItemKey: dmpCareItemKeySchema.optional(),
  patient: DmpPatientContextSchema,
})
export type DmpAlimentationEtatRetenirInput = z.infer<typeof dmpAlimentationEtatRetenirSchema>
